import React, { useState } from 'react';
import styles from './contact.module.css';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    agree: false
  });

  const whatsappNumber = '919365484564';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.agree) {
      alert('Please agree to the booking policies and terms.');
      return;
    }
    const messageText = `🏕️ *Dima Niwas Campsite Enquiry*\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage: ${formData.message}`;
    const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(messageText)}`;
    window.open(whatsappLink, '_blank');
  };

  return (
    <section className={styles.contactSection}>
      <div className={styles.inner}>

        {/* Section Header */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Get in Touch</h2>
          <p className={styles.sectionSubtitle}>
            Ready for an unforgettable experience? Reach out and we'll plan the perfect stay for you.
          </p>
        </div>

        <div className={styles.container}>
          {/* LEFT — Info + Map */}
          <div className={styles.left}>

            {/* Contact Info Card */}
            <div className={styles.infoCard}>
              <h3 className={styles.cardTitle}>Contact Information</h3>
              <div className={styles.contactRow}>
                <div className={styles.iconBox}><Phone size={18} color="#267444" /></div>
                <div>
                  <a href="tel:+919365484564" className={styles.contactLink}>+91 9365484564</a>
                  <br />
                  <a href="tel:+918011602251" className={styles.contactLink}>+91 8011602251</a>
                </div>
              </div>
              <div className={styles.contactRow}>
                <div className={styles.iconBox}><Mail size={18} color="#267444" /></div>
                <a href="mailto:info@dimaniwascampsite.in" className={styles.contactLink}>
                  info@dimaniwascampsite.in
                </a>
              </div>
              <div className={styles.contactRow}>
                <div className={styles.iconBox}><MapPin size={18} color="#267444" /></div>
                <span className={styles.contactText}>
                  Didung Razi, Umrangso, Dima Hasao (Assam), India
                </span>
              </div>
            </div>

            {/* WhatsApp Card */}
            <div className={styles.whatsappCard}>
              <div className={styles.whatsappHeader}>
                <MessageCircle size={22} color="#25d366" />
                <h3 className={styles.cardTitle}>WhatsApp Booking</h3>
              </div>
              <p className={styles.whatsappText}>
                Chat directly with our campsite experts for quick queries or to customise your stay plan.
              </p>
              <p className={styles.whatsappNumbers}><strong>+91 9365484564 / +91 8011602251</strong></p>
              <p className={styles.whatsappHours}>Mon–Sat: 9 AM – 7 PM &nbsp;|&nbsp; Sunday: 10 AM – 3 PM</p>
              <a
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.whatsappBtn}
              >
                <MessageCircle size={18} />
                Chat on WhatsApp
              </a>
            </div>

            {/* Map */}
            <div className={styles.mapWrapper}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2960.8554725808244!2d92.7263118562555!3d25.511619819908887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x374ff353a40d0597%3A0xc3248ff2a4272264!2sDidung%20Raji%20Umrangso!5e1!3m2!1sen!2sin!4v1766562136092!5m2!1sen!2sin"
                allowFullScreen=""
                loading="lazy"
                title="Campsite Location"
                referrerPolicy="no-referrer-when-downgrade"
                className={styles.map}
              />
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className={styles.right}>
            <div className={styles.formCard}>
              <h3 className={styles.cardTitle}>Send Us a Camping Query</h3>
              <p className={styles.formSubtitle}>Fill in the details below and we'll get back to you on WhatsApp.</p>

              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Your Name</label>
                  <input
                    className={styles.input}
                    type="text"
                    name="name"
                    placeholder="e.g. Rahul Sharma"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Email Address</label>
                  <input
                    className={styles.input}
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Phone Number</label>
                  <input
                    className={styles.input}
                    type="tel"
                    name="phone"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Your Message</label>
                  <textarea
                    className={styles.textarea}
                    name="message"
                    rows="5"
                    placeholder="Tell us your travel dates, group size, or any special requests..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.checkboxRow}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    name="agree"
                    id="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="agree" className={styles.checkboxLabel}>
                    I agree to the booking policies and terms
                  </label>
                </div>

                <button className={styles.submitBtn} type="submit">
                  <Send size={18} />
                  Send Enquiry via WhatsApp
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;